import { logger } from '@/shared/lib/logger';
import { supabase } from '@/shared/lib/supabase';
import type { ApiResponse, ErrorResponse } from '@/shared/types/api';

import { apiClient } from './client';
import { PERSONA_QUERIES } from './graphql';

export type AIProvider = 'flux' | 'imagen' | 'seedream' | 'google' | 'kontext';

export type CompleteDatasetRequest = {
  provider?: AIProvider;
  providerVersion?: string;
  sourceMediaPersonaIds: number[];
  generateMediaCount: number;
};

export type CompleteDatasetResponse = ApiResponse & {
  data: {
    generationIds: number[];
    generation: { credits: number };
    persona_id: number;
    num_images: number;
  };
};

export type PersonaData = {
  id: number;
  name?: string;
  gender?: 'male' | 'female';
  age?: number;
  hairColor?: string;
  eyeColor?: string;
  hairStyle?: string;
  bodyType?: string;
  ethnicity?: string;
  additionalDetails?: string;
  mainImageUrl?: string;
  mainImageUrlThumbnail?: string;
  isMine: boolean;
  isPublic?: boolean;
  isMain?: boolean;
  medias?: MediaPersonaData[]; // Dataset media
  mediaInferences?: MediaInferenceData[]; // Output media
  countTrainingCompleted?: number;
  countTrainingProcessing?: number;
  countImageInferences?: number;
  countVideoInferences?: number;
  countAudioInferences?: number;
};

export type CreatePersonaRequest = {
  name?: string;
  gender?: 'male' | 'female';
  age?: number;
  hairColor?: string;
  eyeColor?: string;
  hairStyle?: string;
  bodyType?: string;
  ethnicity?: string;
  additionalDetails?: string;
};

export type CreatePersonaResponse = ApiResponse & {
  data: {
    persona: PersonaData;
  };
};

export type GetPersonasResponse = ApiResponse & {
  data: {
    personas: PersonaData[];
    pageInfo?: {
      nextCursor?: string;
      hasNextPage: boolean;
    };
  };
};

export type GetPersonaResponse = {
  data: {
    persona: PersonaData;
  };
};

export type UpdatePersonaRequest = {
  name?: string;
  gender?: 'male' | 'female'; // Note: API doesn't accept 'randomize', we convert it before sending
  age?: number;
  hairColor?: string;
  eyeColor?: string;
  hairStyle?: string;
  bodyType?: string;
  ethnicity?: string;
  additionalDetails?: string;
};

export type UpdatePersonaResponse = ApiResponse & {
  data: {
    persona: PersonaData;
  };
};

export type MediaPersonaData = {
  id: number;
  personaId?: number;
  name: string;
  url: string;
  urlThumbnail?: string;
  extension?: string;
};

export type MediaInferenceData = {
  id: number;
  personaId?: number;
  name: string;
  url: string;
  urlThumbnail?: string;
  extension?: string;
  inferenceId?: number;
  mediaType?: 'image' | 'video' | 'audio';
  prompt?: string;
  createdAt?: string;
  displayName?: string;
  duration?: number;
};

export type UploadPersonaMediaRequest = {
  personaId: number;
  name: string;
  originalName: string;
  extension: string;
};

export type CreateMediaInputRequest = {
  personaId: number;
  name: string;
  originalName: string;
  extension: string;
  isNSFW?: boolean;
};

export type MediaInputData = {
  id: number;
  name: string;
  originalName: string;
  url: string;
  urlThumbnail: string;
  extension: string;
  personaId: number;
  createdAt?: string;
};

export type CreateMediaInputResponse = ApiResponse & {
  data: {
    id: number;
    uploadUrl: string;
  };
};

export type UploadPersonaMediaResponse = ApiResponse & {
  data: {
    mediaPersona: MediaPersonaData;
  };
};

export class PersonaService {
  // Storage bucket name - matches S3_BUCKET_NAME=famecreator
  private static readonly STORAGE_BUCKET = 'famecreator';

  /**
   * Upload file to Supabase Storage (following Flutter logic)
   */
  private static async uploadFileToStorage(file: File, filePath: string): Promise<string> {
    try {
      const { error } = await supabase.storage
        .from(PersonaService.STORAGE_BUCKET)
        .upload(filePath, file, {
          upsert: false, // Don't overwrite existing files
        });

      if (error) {
        throw new Error(`Storage upload failed: ${error.message}`);
      }

      logger.api.debug('File uploaded successfully', { filePath });
      return filePath;
    } catch (error: unknown) {
      logger.api.error('Error uploading file to storage', { error, filePath });
      throw error;
    }
  }

  /**
   * Generate file path for persona media with user prefix
   * Format: user/{userId}/persona/{personaId}/source/{fileName}
   */
  static generateFilePath(fileName: string, persona: PersonaData, userId: string): string {
    return `user/${userId}/persona/${persona.id}/source/${fileName}`;
  }

  /**
   * Generate file path for NSFW input media with user prefix
   * Format: user/{userId}/persona/{personaId}/inference/{fileName}
   */
  static generateInputFilePath(fileName: string, persona: PersonaData, userId: string): string {
    return `user/${userId}/persona/${persona.id}/inference/${fileName}`;
  }

  /**
   * Upload multiple persona media files in batches of 5
   */
  async uploadMultiplePersonaMedia(
    files: File[],
    persona: PersonaData,
    userId: string,
    onProgress?: (current: number, total: number, fileName: string) => void,
  ): Promise<{
    successful: MediaPersonaData[];
    failed: { file: File; error: string }[];
  }> {
    const successful: MediaPersonaData[] = [];
    const failed: { file: File; error: string }[] = [];

    // Get existing media file names for duplicate validation
    const existingMediaNames = new Set(persona.medias?.map((media) => media.name) || []);

    const BATCH_SIZE = 5;
    const totalFiles = files.length;
    let processedFiles = 0;

    // Process files in batches of 5
    for (let batchStart = 0; batchStart < files.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, files.length);
      const currentBatch = files.slice(batchStart, batchEnd);

      logger.api.info('Processing batch', {
        batchNumber: Math.floor(batchStart / BATCH_SIZE) + 1,
        totalBatches: Math.ceil(files.length / BATCH_SIZE),
        filesInBatch: currentBatch.length,
      });

      // Process all files in current batch in parallel
      const batchPromises = currentBatch.map(async (file, batchIndex) => {
        try {
          // Report progress for current file
          onProgress?.(processedFiles + batchIndex + 1, totalFiles, file.name);

          // Validate file before upload
          const validationError = this.validateFileForUpload(file, existingMediaNames);
          if (validationError) {
            return { success: false, file, error: validationError };
          }

          // Generate unique file name and path
          const uniqueFileName = this.generateUniqueFileName(file.name);
          const filePath = PersonaService.generateFilePath(uniqueFileName, persona, userId);

          // 1. Upload actual file to Supabase Storage first
          await PersonaService.uploadFileToStorage(file, filePath);

          // 2. Then create media record via API
          const response = await this.uploadPersonaMedia({
            personaId: persona.id,
            name: uniqueFileName,
            originalName: file.name,
            extension: this.getFileExtension(file.name),
          });

          if (response.data?.mediaPersona) {
            // Add to existing names to prevent duplicates in same batch
            existingMediaNames.add(uniqueFileName);
            return { success: true, file, data: response.data.mediaPersona };
          } else {
            return { success: false, file, error: 'No media data returned from API' };
          }
        } catch (error: unknown) {
          return {
            success: false,
            file,
            error: error instanceof Error ? error.message : 'Upload failed',
          };
        }
      });

      // Wait for all files in current batch to complete
      const batchResults = await Promise.all(batchPromises);

      // Process batch results
      batchResults.forEach((result) => {
        if (result.success) {
          successful.push(result.data!);
        } else {
          failed.push({ file: result.file, error: result.error || 'Unknown error' });
        }
      });

      processedFiles += currentBatch.length;
      logger.api.info('Batch completed', {
        batchNumber: Math.floor(batchStart / BATCH_SIZE) + 1,
        successful: batchResults.filter((r) => r.success).length,
        total: currentBatch.length,
      });

      // Small delay between batches to avoid overwhelming the server
      if (batchEnd < files.length) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    logger.api.info('Upload complete', {
      successful: successful.length,
      failed: failed.length,
      total: totalFiles,
    });
    return { successful, failed };
  }

  /**
   * Validate file before upload
   */
  private validateFileForUpload(file: File, existingMediaNames: Set<string>): string | null {
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      return `File "${file.name}" is too large. Maximum size is 10MB.`;
    }

    // Check file type (support both JPEG and JPG MIME types)
    const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return `File "${file.name}" has unsupported format. Only JPG, JPEG, PNG, and WebP are allowed.`;
    }

    // Check for duplicates by original name (basic check)
    // Note: This is a simple check - in practice, content-based deduplication would be better
    const duplicateMedia = Array.from(existingMediaNames).find(
      (mediaName) => this.extractOriginalName(mediaName) === this.extractOriginalName(file.name),
    );

    if (duplicateMedia) {
      return `File "${file.name}" appears to be a duplicate of existing media.`;
    }

    return null; // No validation errors
  }

  /**
   * Extract original name from generated unique name
   */
  private extractOriginalName(fileName: string): string {
    // Remove UUID and timestamp from generated names like "photo_uuid.jpg"
    const parts = fileName.split('_');
    if (parts.length >= 2) {
      // Keep only the first part (original name without extension)
      const nameWithoutExt = parts[0];
      const extension = fileName.split('.').pop();
      return `${nameWithoutExt}.${extension}`;
    }
    return fileName;
  }

  /**
   * Generate unique file name to avoid conflicts
   */
  private generateUniqueFileName(originalName: string): string {
    const extension = originalName.split('.').pop();
    const uuid = crypto.randomUUID();

    return `${uuid}.${extension}`;
  }

  /**
   * Download image from URL and convert to File
   */
  static async downloadImageAsFile(imageUrl: string, fileName?: string): Promise<File> {
    try {
      // Use backend proxy to bypass CORS
      const API_BASE_URL = import.meta.env.VITE_EXTERNAL_API_URL || 'https://api.preprod.kupid.ai';
      const proxyUrl = `${API_BASE_URL}/external/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.statusText}`);
      }

      const blob = await response.blob();
      const mimeType = blob.type;

      // Determine file extension from MIME type or URL
      let extension = 'png'; // default
      if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
        extension = 'jpg';
      } else if (mimeType.includes('png')) {
        extension = 'png';
      } else if (mimeType.includes('webp')) {
        extension = 'webp';
      }

      // Generate filename if not provided
      const finalFileName = fileName || `nsfw_generated_${Date.now()}.${extension}`;

      return new File([blob], finalFileName, { type: mimeType });
    } catch (error: unknown) {
      logger.api.error('Failed to download image from URL', { error, imageUrl });
      throw error;
    }
  }

  /**
   * Get file extension from filename
   */
  private getFileExtension(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (ext === 'jpeg') {
      return 'jpg';
    }
    return ext;
  }

  /**
   * Create a new persona
   * POST /persona
   */
  async createPersona(data?: CreatePersonaRequest): Promise<CreatePersonaResponse> {
    try {
      const response = await apiClient.post<CreatePersonaResponse>('/persona', data || {});
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get personas with pagination via GraphQL
   */
  async getPersonas(limit: number = 16, cursor?: string): Promise<GetPersonasResponse> {
    try {
      const response = await apiClient.post<{
        data: {
          personas: {
            edges: Array<{ node: PersonaData }>;
            pageInfo: { nextCursor: string | null; hasNextPage: boolean };
          };
        };
      }>('/graphql', {
        query: PERSONA_QUERIES.GET_PERSONAS,
        variables: { limit, cursor },
      });

      // Transform paginated response to match expected format
      const paginatedData = response.data.data.personas;
      const personas = paginatedData.edges.map((edge) => edge.node);

      return {
        statusCode: 200,
        type: 'success' as const,
        data: {
          personas,
          pageInfo: {
            nextCursor: paginatedData.pageInfo.nextCursor || undefined,
            hasNextPage: paginatedData.pageInfo.hasNextPage,
          },
        },
      };
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get single persona via GraphQL
   */
  async getPersona(id: number): Promise<GetPersonaResponse> {
    try {
      const response = await apiClient.post<{
        data: {
          persona: PersonaData & {
            medias?: {
              edges: Array<{ node: MediaPersonaData }>;
            };
          };
        };
      }>('/graphql', {
        query: PERSONA_QUERIES.GET_PERSONA,
        variables: { id },
      });

      // Transform medias from edges structure to array
      if (response.data?.data?.persona?.medias?.edges) {
        const mediasArray = response.data.data.persona.medias.edges.map(
          (edge: { node: MediaPersonaData }) => edge.node,
        );
        (response.data.data.persona.medias as MediaPersonaData[]) = mediasArray;
      }

      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Update persona
   * PATCH /persona/{id}
   */

  async updatePersona(id: number, data: UpdatePersonaRequest): Promise<UpdatePersonaResponse> {
    try {
      const response = await apiClient.patch<UpdatePersonaResponse>(`/persona/${id}`, data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Delete persona
   * DELETE /persona/{id}
   */

  async deletePersona(id: number): Promise<ApiResponse> {
    try {
      const response = await apiClient.delete<ApiResponse>(`/persona/${id}`);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Upload media for persona
   * POST /persona/media
   */
  async uploadPersonaMedia(data: UploadPersonaMediaRequest): Promise<UploadPersonaMediaResponse> {
    try {
      const response = await apiClient.post<UploadPersonaMediaResponse>('/persona/media', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Create media input for NSFW images
   * POST /media-input (BCM Best Creator backend)
   */
  async createMediaInput(data: CreateMediaInputRequest): Promise<CreateMediaInputResponse> {
    try {
      const response = await apiClient.post<CreateMediaInputResponse>('/media-inference', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Upload media (image/audio) for Studio (creates MediaInference)
   */
  async uploadMediaInference(
    file: File,
    persona: PersonaData,
    mediaType: 'image' | 'audio' | 'video' = 'image',
  ): Promise<{ mediaInference: MediaInferenceData }> {
    try {
      const uniqueFileName = `${crypto.randomUUID()}.${this.getFileExtension(file.name)}`;

      // 1. Upload to Supabase storage
      const filePath = PersonaService.generateInferenceFilePath(uniqueFileName, persona);
      await PersonaService.uploadFileToStorage(file, filePath);

      // 2. Create MediaInference record via API
      const response = await apiClient.post<{
        data: {
          mediaInference: MediaInferenceData;
        };
      }>('/media-inference', {
        personaId: persona.id,
        name: uniqueFileName,
        originalName: file.name,
        extension: this.getFileExtension(file.name),
        mediaType,
        isNSFW: false, // Default to false for uploaded media
      });

      if (response.data?.data?.mediaInference) {
        return { mediaInference: response.data.data.mediaInference };
      } else {
        throw new Error('No media inference data returned from API');
      }
    } catch (error: unknown) {
      logger.api.error(`Failed to upload ${mediaType} for studio`, {
        error,
        fileName: file.name,
        personaId: persona.id,
      });
      throw error;
    }
  }

  /**
   * Upload NSFW image specifically for NSFW Image Studio
   * Creates MediaInference directly via /media-inference
   */
  async uploadNSFWImageForStudio(
    imageUrl: string,
    persona: PersonaData,
  ): Promise<{ mediaInference: MediaInferenceData }> {
    try {
      // 1. Download image from external URL
      const uniqueFileName = `${crypto.randomUUID()}.png`;
      const file = await PersonaService.downloadImageAsFile(imageUrl, uniqueFileName);

      // 2. Upload directly to our Supabase storage (inference path)
      const filePath = PersonaService.generateInferenceFilePath(file.name, persona);
      await PersonaService.uploadFileToStorage(file, filePath);

      // 3. Create MediaInference record via /media-inference API
      const response = await apiClient.post<{
        data: {
          mediaInference: MediaInferenceData;
        };
      }>('/media-inference', {
        personaId: persona.id,
        name: file.name,
        extension: this.getFileExtension(file.name),
        isNSFW: true,
      });

      if (response.data?.data?.mediaInference) {
        const mediaInference = response.data.data.mediaInference;
        return {
          mediaInference: {
            id: mediaInference.id,
            name: mediaInference.name,
            extension: mediaInference.extension,
            personaId: mediaInference.personaId,
            url: mediaInference.url,
            urlThumbnail: mediaInference.urlThumbnail || '',
            inferenceId: mediaInference.inferenceId || undefined,
          },
        };
      } else {
        throw new Error('No media inference data returned from API');
      }
    } catch (error: unknown) {
      logger.api.error('Failed to upload NSFW image for studio', {
        error,
        imageUrl,
        personaId: persona.id,
      });
      throw error;
    }
  }

  /**
   * Generate file path for inference (not input)
   */
  static generateInferenceFilePath(fileName: string, persona: PersonaData): string {
    return `persona/${persona.id}/inference/${fileName}`;
  }

  /**
   * Upload NSFW image from external URL to our Supabase
   * Combined flow: download -> upload to our Supabase -> create media_input -> create inference
   */
  async uploadNSFWImage(
    imageUrl: string,
    persona: PersonaData,
    originalPrompt?: string,
  ): Promise<MediaInputData> {
    try {
      // 1. Download image from external URL
      const uniqueFileName = `${crypto.randomUUID()}.png`;
      const file = await PersonaService.downloadImageAsFile(imageUrl, uniqueFileName);

      // 2. Upload directly to our Supabase storage
      // TODO: Update this when NSFW logic is refactored
      const tempUserId = 'temp-user-id'; // Temporary fix for NSFW methods
      const filePath = PersonaService.generateInputFilePath(file.name, persona, tempUserId);
      await PersonaService.uploadFileToStorage(file, filePath);

      // 3. Create media_input record via API with NSFW flag
      const response = await this.createMediaInput({
        personaId: persona.id,
        name: file.name,
        originalName: originalPrompt ? `${originalPrompt.substring(0, 50)}.png` : file.name,
        extension: this.getFileExtension(file.name) as 'jpg' | 'png' | 'webp',
        isNSFW: true, // Mark as NSFW since this is from NSFW image studio
      });

      if (response.data?.id) {
        // Return MediaInputData format for compatibility
        return {
          id: response.data.id,
          name: file.name,
          originalName: originalPrompt ? `${originalPrompt.substring(0, 50)}.png` : file.name,
          url: '', // Will be populated when fetched via GraphQL from our Supabase
          urlThumbnail: '',
          extension: this.getFileExtension(file.name),
          personaId: persona.id,
          createdAt: new Date().toISOString(),
        };
      } else {
        throw new Error('No media input data returned from API');
      }
    } catch (error: unknown) {
      logger.api.error('Failed to upload NSFW image', {
        error,
        imageUrl,
        personaId: persona.id,
      });
      throw error;
    }
  }

  /**
   * Delete persona media
   * DELETE /persona/media/{id}
   */
  async deletePersonaMedia(id: number): Promise<ApiResponse> {
    try {
      const response = await apiClient.delete<ApiResponse>(`/persona/media/${id}`);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  // REMOVED: generatePersonaImages - replaced with /inference API

  /**
   * Complete persona dataset with additional generated images
   * POST /persona/{id}/complete-dataset
   */
  async completePersonaDataset(
    personaId: number,
    request: CompleteDatasetRequest,
  ): Promise<CompleteDatasetResponse> {
    const requestBody = {
      provider: request.provider || 'kontext',
      sourceMediaPersonaIds: request.sourceMediaPersonaIds,
      generateMediaCount: request.generateMediaCount,
    };

    try {
      // console.log('🎨 Completing persona dataset:', { personaId, requestBody });
      // console.log('🎨 Request body details:', {
      //   provider: requestBody.provider,
      //   sourceMediaPersonaIds: requestBody.sourceMediaPersonaIds,
      //   datasetSize: requestBody.datasetSize,
      //   sourceIdsType: typeof requestBody.sourceMediaPersonaIds,
      //   sourceIdsLength: requestBody.sourceMediaPersonaIds?.length
      // });

      const response = await apiClient.post<CompleteDatasetResponse>(
        `/persona/${personaId}/complete-dataset`,
        requestBody,
      );

      // console.log('✅ Complete dataset API response:', response.data);
      return response.data;
    } catch (error: unknown) {
      logger.api.error('Complete dataset API error', {
        error,
        personaId,
        requestBody,
      });
      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = (error as { response: { data: ErrorResponse } }).response.data;
        logger.api.error('Complete dataset error details', {
          errorResponse,
          validationErrors: errorResponse.errors,
        });
        throw errorResponse;
      }
      throw error;
    }
  }

  /**
   * Delete persona media
   */
  async deleteMediaPersona(mediaPersonaId: number): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.delete(`/persona/media/${mediaPersonaId}`);
      return { success: true };
    } catch (error: unknown) {
      logger.api.error('Failed to delete persona media', {
        error,
        mediaPersonaId,
      });
      return {
        success: false,
        error:
          (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to delete media',
      };
    }
  }

  /**
   * Get media inferences for a persona with optional filtering
   */
  async getMediaInferences(
    personaId: number,
    mediaType?: 'image' | 'video' | 'audio',
    limit: number = 20,
    cursor?: string,
  ): Promise<{
    mediaInferences: MediaInferenceData[];
    pageInfo?: { nextCursor?: string; hasNextPage: boolean };
    error?: string;
  }> {
    try {
      const response = await apiClient.post<{
        data?: {
          mediaInferences: {
            edges: Array<{
              node: MediaInferenceData;
            }>;
            pageInfo: {
              nextCursor?: string;
              hasNextPage: boolean;
            };
          };
        };
        errors?: Array<{ message: string }>;
      }>('/graphql', {
        query: PERSONA_QUERIES.GET_MEDIA_INFERENCES,
        variables: {
          personaId: personaId.toString(),
          mediaType,
          limit,
          cursor,
        },
      });

      if (response.data?.errors) {
        logger.api.error('GraphQL errors in getMediaInferences', {
          errors: response.data.errors,
          personaId,
          mediaType,
        });
        return {
          mediaInferences: [],
          error: response.data.errors[0]?.message || 'GraphQL query failed',
        };
      }

      const data = response.data?.data?.mediaInferences;
      if (!data) {
        return {
          mediaInferences: [],
          error: 'No media inferences data returned',
        };
      }

      const mediaInferences: MediaInferenceData[] = data.edges.map(
        (edge: { node: MediaInferenceData }) => ({
          id: edge.node.id,
          personaId: edge.node.personaId,
          name: edge.node.name,
          url: edge.node.url,
          urlThumbnail: edge.node.urlThumbnail,
          extension: edge.node.extension,
          inferenceId: edge.node.inferenceId,
          createdAt: edge.node.createdAt,
          mediaType, // Set from filter parameter or infer from extension
        }),
      );

      return {
        mediaInferences,
        pageInfo: data.pageInfo,
      };
    } catch (error: unknown) {
      logger.api.error('Failed to get media inferences', {
        error,
        personaId,
        mediaType,
      });
      return {
        mediaInferences: [],
        error: error instanceof Error ? error.message : 'Failed to get media inferences',
      };
    }
  }
}

// Export singleton instance
export const personaService = new PersonaService();
